// Libs
import clsx from "clsx";
import { type FormikErrors, useField } from "formik";

// Types
import type { TFunction } from "src/design-system/core/TranslationContext";
import type { OfferTypeEnum } from "src/types/offer";
import { OfferDiscountType } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.formator";

// Utils
import { getColorForSpecificValues } from "src/components-ds/hourly-views/common/HeatMap.logic";
import type { CategoryItemList } from "src/types/offer/CategoryItemList";
import { formatDiscount } from "src/utils/formatters/offers";
import { formatPercentValueForScenarios } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";
import { renderOfferTypeTooltipWithProducts } from "src/views/offer/offer-tables-view/tooltips/renderOfferTypeTooltip";

// Hooks
import { useTranslation } from "src/design-system/core/TranslationContext";
import { useFormatOptions } from "src/hooks";
import { useScenarioCardStyles } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard.styles";

// Components
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Percent from "@mui/icons-material/Percent";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import { Box, capitalize, Tooltip } from "@mui/material";
import { X as CrossIcon } from "react-feather";
import { IsSmartSchedulerIcon } from "src/components/Icons";
import { ScenarioDisplayLine } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioDisplayLine";

export interface DisplayScenario {
    id: number;
    trueId?: number;
    triggerMin?: number;
    triggerMax?: number;
    isTemplate: boolean;
    offerType: OfferTypeEnum;
    typeDiscount: OfferDiscountType;
    discountValue: number;
    cart: number;
    audienceName?: string;
    categoryItemList?: Partial<CategoryItemList>[];
    products?: {
        itemName: string;
        categoryName: string;
        discountValue?: number;
    }[];
}

interface ScenarioDisplayProps {
    scenario: DisplayScenario;
    setIdDeleteScenario: (id: number) => void;
    handleOpenDialogUpdate: (id: number) => void;
    isModificationAllowed: boolean;
    t: TFunction;
    error: FormikErrors<any>;
    setValueScenario?: (b: boolean) => void;
    tooltipDisableScenario: () => string;
    isScenarioDisabled: boolean;
    scenarioList: DisplayScenario[];
}

export function ScenarioDisplayWithFormik(props: ScenarioDisplayProps): JSX.Element {
    const [fieldScenario, _, helperScenario] = useField(`scenarios[${props.scenario.id}]`);
    function setValueScenario(isHover: boolean) {
        helperScenario.setValue({ ...fieldScenario.value, isHover });
    }
    return <ScenarioDisplay {...props} setValueScenario={setValueScenario} />;
}

export function ScenarioDisplay({
    setIdDeleteScenario,
    scenario,
    handleOpenDialogUpdate,
    isModificationAllowed,
    t,
    error,
    tooltipDisableScenario,
    isScenarioDisabled,
    setValueScenario,
    scenarioList,
}: ScenarioDisplayProps): JSX.Element {
    const classes = useScenarioCardStyles();
    const { translateCommon } = useTranslation();
    const formatOptions = useFormatOptions();
    const formattedItems = scenario.products?.map((product) => ({
        name: product.itemName,
        categoryName: product.categoryName,
        discount: {
            percentDiscount: product.discountValue,
        },
    }));

    const handleDelete = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setIdDeleteScenario(scenario.id);
        e.stopPropagation();
    };

    const handleClickOnCard = () => {
        handleOpenDialogUpdate(scenario.id);
    };

    const tooltipText = renderOfferTypeTooltipWithProducts(
        undefined,
        {
            items: scenario.products?.map((elm) => {
                return {
                    name: elm.itemName,
                    categoryName: elm.categoryName,
                    percentDiscount: elm.discountValue,
                };
            }),
        },
        formatOptions
    );

    const isWarningCellName = !!error && scenario.isTemplate === false;

    const isScenarioProductList =
        tooltipText === "" || scenario.categoryItemList?.length > 0 ? false : true;
    const color = getColorForSpecificValues(
        scenario.id.toString(),
        scenarioList.length,
        isWarningCellName
    );

    const nameTitleClassName = clsx(classes.scenarioTitle, {
        [classes.ScenarioDisplayTitleWarningBordered]: isWarningCellName,
    });
    const textTitleClassName = clsx({
        [classes.ScenarioDisplayTitleWarning]: isWarningCellName,
        [classes.ScenarioDisplayTitle]: !isWarningCellName,
    });

    return (
        <Tooltip title={tooltipDisableScenario()}>
            <Box
                className={clsx({
                    [classes.ScenarioCard]: true,
                    [classes.ScenarioCardHover]: isModificationAllowed !== false,
                    [classes.ScenarioCardDisabled]: isScenarioDisabled === true,
                })}
                onClick={isScenarioDisabled === false ? handleClickOnCard : undefined}
                onMouseEnter={
                    setValueScenario
                        ? () => {
                              setValueScenario(true);
                          }
                        : null
                }
                onMouseLeave={
                    setValueScenario
                        ? () => {
                              setValueScenario(false);
                          }
                        : null
                }
            >
                <Box className={classes.ScenarioCardTitleContainer}>
                    <Box className={classes.ScenarioTitleActionAlign}>
                        <Box
                            className={nameTitleClassName}
                            style={{
                                backgroundColor: color,
                            }}
                        >
                            {
                                <span className={textTitleClassName}>
                                    {capitalize(translateCommon("concepts.scenario"))}{" "}
                                    {scenario.id + 1}
                                </span>
                            }
                        </Box>

                        {isModificationAllowed !== false && (
                            <Box
                                onClick={handleDelete}
                                className={classes.ScenarioDisplayCrossContainer}
                            >
                                <CrossIcon className={classes.ScenarioDisplayCross} />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box className={classes.ScenarioCardIntervalContainer}>
                    <ScenarioDisplayLine
                        isPrice={false}
                        icon={<IsSmartSchedulerIcon className={classes.ScenarioDisplayLineIcon} />}
                        text={t("smart-card-interval")}
                        subText={
                            scenario.triggerMin !== undefined && scenario.triggerMax !== undefined
                                ? t("smart-card-interval-values", {
                                      valueOne: formatPercentValueForScenarios(scenario.triggerMin),
                                      valueTwo: formatPercentValueForScenarios(scenario.triggerMax),
                                  })
                                : "-"
                        }
                    />
                </Box>
                {!scenario.isTemplate ? (
                    <>
                        <Tooltip
                            title={
                                scenario.categoryItemList?.length > 0
                                    ? scenario.categoryItemList.map((elm) => elm.name).join(", ")
                                    : renderOfferTypeTooltipWithProducts(
                                          undefined,
                                          { items: formattedItems },
                                          formatOptions
                                      )
                            }
                        >
                            <Box
                                className={clsx({
                                    [classes.ScenarioDisplayLineValueContainer]: true,
                                    [classes.ScenarioProductList]: isScenarioProductList === true,
                                })}
                            >
                                <ScenarioDisplayLine
                                    isPrice={false}
                                    error={error}
                                    icon={
                                        <RestaurantMenuIcon
                                            className={classes.ScenarioDisplayLineIcon}
                                        />
                                    }
                                    text={t("smart-card-offer-type")}
                                    subText={
                                        scenario.offerType
                                            ? translateCommon(
                                                  `enums.offer-type.${scenario.offerType}`
                                              )
                                            : "-"
                                    }
                                />
                            </Box>
                        </Tooltip>

                        <Box className={classes.ScenarioDisplayLineValueContainer}>
                            <ScenarioDisplayLine
                                isPrice={false}
                                icon={<Percent className={classes.ScenarioDisplayLineIcon} />}
                                text={t("smart-card-discount")}
                                subText={
                                    scenario.offerType
                                        ? formatDiscount(
                                              {
                                                  type: scenario.offerType as OfferTypeEnum,
                                                  discount: {
                                                      flatDiscount:
                                                          scenario.typeDiscount ===
                                                              OfferDiscountType.FlatDiscount &&
                                                          scenario.discountValue,
                                                      percentDiscount:
                                                          scenario.typeDiscount !==
                                                              OfferDiscountType.FlatDiscount &&
                                                          scenario.discountValue,
                                                  },
                                              },
                                              formatOptions
                                          )
                                        : "-"
                                }
                            />
                        </Box>
                        <Box className={classes.ScenarioDisplayLineValueContainer}>
                            <ScenarioDisplayLine
                                isPrice={true}
                                icon={
                                    <ShoppingBasketOutlinedIcon
                                        className={classes.ScenarioDisplayLineIcon}
                                    />
                                }
                                text={t("smart-card-min-basket")}
                                subText={scenario?.cart ?? "-"}
                            />
                        </Box>
                        <Box>
                            <ScenarioDisplayLine
                                isPrice={false}
                                icon={
                                    <PeopleOutlinedIcon
                                        className={classes.ScenarioDisplayLineIcon}
                                    />
                                }
                                text={t("smart-card-audience")}
                                subText={
                                    scenario.audienceName
                                        ? translateCommon(`enums.audience.${scenario.audienceName}`)
                                        : "-"
                                }
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box textAlign="center" sx={{ mt: 2 }}>
                            <LocalOfferOutlinedIcon
                                className={`${classes.ScenarioMissingOfferDisplayLineIcon} ${
                                    isScenarioDisabled === false
                                        ? classes.ScenarioPrimaryColor
                                        : classes.ScenarioDisabledColor
                                }`}
                            />
                        </Box>

                        <Box textAlign="center" sx={{ mt: 1 }}>
                            <span className={classes.templateScenarioText}>
                                {t("smart-scheduler.missing-offer")}
                            </span>
                        </Box>

                        <Box textAlign="center" sx={{ mt: 1 }} width={150}>
                            <span
                                className={`${classes.templateScenarioText} ${
                                    isScenarioDisabled === false
                                        ? classes.ScenarioPrimaryColor
                                        : classes.ScenarioDisabledColor
                                }`}
                            >
                                {t("smart-scheduler.fill-scenario")}
                            </span>
                        </Box>
                    </>
                )}
            </Box>
        </Tooltip>
    );
}
